import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type {
  PlrAssessment13849,
  PlrProbabilityOfOccurrence,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { cn } from '@/shared/lib/utils'

type PlrSistemaKey = 'plA' | 'plB' | 'plC' | 'plD' | 'plE'
type PlrGrade = 'A' | 'B' | 'C' | 'D' | 'E'

const PLR_FILL: Record<PlrSistemaKey, string> = {
  plA: '#10b981',
  plB: '#84cc16',
  plC: '#eab308',
  plD: '#f97316',
  plE: '#ef4444',
}

const PLR_TEXT: Record<PlrSistemaKey, string> = {
  plA: '#000000',
  plB: '#000000',
  plC: '#000000',
  plD: '#000000',
  plE: '#000000',
}

const GRADE_TO_PLR_KEY: Record<PlrGrade, PlrSistemaKey> = {
  A: 'plA',
  B: 'plB',
  C: 'plC',
  D: 'plD',
  E: 'plE',
}

function reducePlrGradeByOne(grade: PlrGrade): PlrGrade {
  switch (grade) {
    case 'E':
      return 'D'
    case 'D':
      return 'C'
    case 'C':
      return 'B'
    case 'B':
    case 'A':
      return 'A'
  }
}

type GraphNode = {
  id: string
  label: string
  centerX: number
  centerY: number
  plrKey?: PlrSistemaKey
}

type GraphPath = {
  key: string
  severity: 'S1' | 'S2'
  frequency: 'F1' | 'F2'
  possibility: 'P1' | 'P2'
  plrGrade: PlrGrade
  nodeIds: [string, string, string, string]
  lowPlrNodeId: string
}

const PLR_HIGH_COLUMN_X = 232
const PLR_LOW_COLUMN_X = 300

const GRAPH_PATHS: GraphPath[] = [
  {
    key: 'S1F1P1',
    severity: 'S1',
    frequency: 'F1',
    possibility: 'P1',
    plrGrade: 'A',
    nodeIds: ['s1', 's1f1', 's1f1p1', 'pla'],
    lowPlrNodeId: 'pla-low',
  },
  {
    key: 'S1F1P2',
    severity: 'S1',
    frequency: 'F1',
    possibility: 'P2',
    plrGrade: 'B',
    nodeIds: ['s1', 's1f1', 's1f1p2', 'plb1'],
    lowPlrNodeId: 'plb1-low',
  },
  {
    key: 'S1F2P1',
    severity: 'S1',
    frequency: 'F2',
    possibility: 'P1',
    plrGrade: 'B',
    nodeIds: ['s1', 's1f2', 's1f2p1', 'plb2'],
    lowPlrNodeId: 'plb2-low',
  },
  {
    key: 'S1F2P2',
    severity: 'S1',
    frequency: 'F2',
    possibility: 'P2',
    plrGrade: 'C',
    nodeIds: ['s1', 's1f2', 's1f2p2', 'plc1'],
    lowPlrNodeId: 'plc1-low',
  },
  {
    key: 'S2F1P1',
    severity: 'S2',
    frequency: 'F1',
    possibility: 'P1',
    plrGrade: 'C',
    nodeIds: ['s2', 's2f1', 's2f1p1', 'plc2'],
    lowPlrNodeId: 'plc2-low',
  },
  {
    key: 'S2F1P2',
    severity: 'S2',
    frequency: 'F1',
    possibility: 'P2',
    plrGrade: 'D',
    nodeIds: ['s2', 's2f1', 's2f1p2', 'pld1'],
    lowPlrNodeId: 'pld1-low',
  },
  {
    key: 'S2F2P1',
    severity: 'S2',
    frequency: 'F2',
    possibility: 'P1',
    plrGrade: 'D',
    nodeIds: ['s2', 's2f2', 's2f2p1', 'pld2'],
    lowPlrNodeId: 'pld2-low',
  },
  {
    key: 'S2F2P2',
    severity: 'S2',
    frequency: 'F2',
    possibility: 'P2',
    plrGrade: 'E',
    nodeIds: ['s2', 's2f2', 's2f2p2', 'ple'],
    lowPlrNodeId: 'ple-low',
  },
]

const GRAPH_NODES: GraphNode[] = [
  { id: 's1', label: 'S1', centerX: 28, centerY: 64 },
  { id: 's2', label: 'S2', centerX: 28, centerY: 192 },
  { id: 's1f1', label: 'F1', centerX: 96, centerY: 32 },
  { id: 's1f2', label: 'F2', centerX: 96, centerY: 96 },
  { id: 's2f1', label: 'F1', centerX: 96, centerY: 160 },
  { id: 's2f2', label: 'F2', centerX: 96, centerY: 224 },
  { id: 's1f1p1', label: 'P1', centerX: 164, centerY: 16 },
  { id: 's1f1p2', label: 'P2', centerX: 164, centerY: 48 },
  { id: 's1f2p1', label: 'P1', centerX: 164, centerY: 80 },
  { id: 's1f2p2', label: 'P2', centerX: 164, centerY: 112 },
  { id: 's2f1p1', label: 'P1', centerX: 164, centerY: 144 },
  { id: 's2f1p2', label: 'P2', centerX: 164, centerY: 176 },
  { id: 's2f2p1', label: 'P1', centerX: 164, centerY: 208 },
  { id: 's2f2p2', label: 'P2', centerX: 164, centerY: 240 },
  { id: 'pla', label: 'a', centerX: PLR_HIGH_COLUMN_X, centerY: 16, plrKey: 'plA' },
  { id: 'plb1', label: 'b', centerX: PLR_HIGH_COLUMN_X, centerY: 48, plrKey: 'plB' },
  { id: 'plb2', label: 'b', centerX: PLR_HIGH_COLUMN_X, centerY: 80, plrKey: 'plB' },
  { id: 'plc1', label: 'c', centerX: PLR_HIGH_COLUMN_X, centerY: 112, plrKey: 'plC' },
  { id: 'plc2', label: 'c', centerX: PLR_HIGH_COLUMN_X, centerY: 144, plrKey: 'plC' },
  { id: 'pld1', label: 'd', centerX: PLR_HIGH_COLUMN_X, centerY: 176, plrKey: 'plD' },
  { id: 'pld2', label: 'd', centerX: PLR_HIGH_COLUMN_X, centerY: 208, plrKey: 'plD' },
  { id: 'ple', label: 'e', centerX: PLR_HIGH_COLUMN_X, centerY: 240, plrKey: 'plE' },
]

const HIGH_PLR_NODE_BY_ID = new Map(
  GRAPH_NODES.filter((node) => node.plrKey != null).map((node) => [node.id, node])
)

const LOW_PLR_NODES: GraphNode[] = GRAPH_PATHS.map((path) => {
  const highPlrNode = HIGH_PLR_NODE_BY_ID.get(path.nodeIds[3])
  const lowGrade = reducePlrGradeByOne(path.plrGrade)
  return {
    id: path.lowPlrNodeId,
    label: lowGrade.toLowerCase(),
    centerX: PLR_LOW_COLUMN_X,
    centerY: highPlrNode?.centerY ?? 0,
    plrKey: GRADE_TO_PLR_KEY[lowGrade],
  }
})

const ALL_GRAPH_NODES: GraphNode[] = [...GRAPH_NODES, ...LOW_PLR_NODES]

const GRAPH_EDGES: Array<{ from: string; to: string }> = [
  ...Array.from(
    new Map(
      GRAPH_PATHS.flatMap((path) => {
        const [sNode, fNode, pNode, plrNode] = path.nodeIds
        return [
          { from: sNode, to: fNode },
          { from: fNode, to: pNode },
          { from: pNode, to: plrNode },
          { from: plrNode, to: path.lowPlrNodeId },
        ]
      }).map((edge) => [`${edge.from}->${edge.to}`, edge] as const)
    ).values()
  ),
]

const NODE_WIDTH = 36
const NODE_HEIGHT = 22
const HEADER_BASELINE_Y = 12
const GRAPH_TOP_OFFSET = 26
const SVG_WIDTH = 336
const SVG_HEIGHT = 268 + GRAPH_TOP_OFFSET

type GraphParameterValue = 'S1' | 'S2' | 'F1' | 'F2' | 'P1' | 'P2'

const GRAPH_PARAMETER_VALUES = new Set<GraphParameterValue>([
  'S1',
  'S2',
  'F1',
  'F2',
  'P1',
  'P2',
])

function isGraphParameterLabel(label: string): label is GraphParameterValue {
  return GRAPH_PARAMETER_VALUES.has(label as GraphParameterValue)
}

function isGraphParameterNode(
  node: GraphNode
): node is GraphNode & { label: GraphParameterValue } {
  return node.plrKey == null && isGraphParameterLabel(node.label)
}

const GRAPH_NODES_OFFSET: GraphNode[] = ALL_GRAPH_NODES.map((node) => ({
  ...node,
  centerY: node.centerY + GRAPH_TOP_OFFSET,
}))

const NODE_BY_ID_OFFSET = new Map(
  GRAPH_NODES_OFFSET.map((node) => [node.id, node])
)

function getAssessmentPathKey(assessment: PlrAssessment13849): string {
  return `${assessment.severity}${assessment.frequency}${assessment.possibilityOfAvoidance}`
}

function getActivePath(assessment: PlrAssessment13849): GraphPath | null {
  const pathKey = getAssessmentPathKey(assessment)
  return GRAPH_PATHS.find((path) => path.key === pathKey) ?? null
}

function getEdgeMidpoints(
  from: GraphNode,
  to: GraphNode
): { x1: number; y1: number; x2: number; y2: number } {
  return {
    x1: from.centerX + NODE_WIDTH / 2,
    y1: from.centerY,
    x2: to.centerX - NODE_WIDTH / 2,
    y2: to.centerY,
  }
}

type EnIso13849RiskGraphProps = {
  assessment: PlrAssessment13849
  probabilityOfOccurrence: PlrProbabilityOfOccurrence
  highOccurrenceLabel: string
  lowOccurrenceLabel: string
  nodeTooltips: Record<GraphParameterValue, string>
  ariaLabel: string
  className?: string
}

export function EnIso13849RiskGraph({
  assessment,
  probabilityOfOccurrence,
  highOccurrenceLabel,
  lowOccurrenceLabel,
  nodeTooltips,
  ariaLabel,
  className,
}: EnIso13849RiskGraphProps) {
  const activePath = getActivePath(assessment)
  const activeNodeIds = new Set<string>()
  const activeEdgeKeys = new Set<string>()
  const finalPlrNodeId =
    activePath == null
      ? null
      : probabilityOfOccurrence === 'LOW'
        ? activePath.lowPlrNodeId
        : activePath.nodeIds[3]

  if (activePath != null) {
    const [sNode, fNode, pNode, highPlrNode] = activePath.nodeIds
    activeNodeIds.add(sNode)
    activeNodeIds.add(fNode)
    activeNodeIds.add(pNode)
    activeNodeIds.add(highPlrNode)
    activeEdgeKeys.add(`${sNode}->${fNode}`)
    activeEdgeKeys.add(`${fNode}->${pNode}`)
    activeEdgeKeys.add(`${pNode}->${highPlrNode}`)

    if (probabilityOfOccurrence === 'LOW') {
      activeNodeIds.add(activePath.lowPlrNodeId)
      activeEdgeKeys.add(`${highPlrNode}->${activePath.lowPlrNodeId}`)
    }
  }

  const parameterNodes = GRAPH_NODES_OFFSET.filter(isGraphParameterNode)

  return (
    <div className={cn('relative w-full max-w-md', className)}>
      <svg
        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        role='img'
        aria-label={ariaLabel}
        className='h-auto w-full text-foreground'
      >
      <text
        x={28}
        y={HEADER_BASELINE_Y}
        textAnchor='middle'
        className='fill-muted-foreground text-[10px] font-medium'
      >
        S
      </text>
      <text
        x={96}
        y={HEADER_BASELINE_Y}
        textAnchor='middle'
        className='fill-muted-foreground text-[10px] font-medium'
      >
        F
      </text>
      <text
        x={164}
        y={HEADER_BASELINE_Y}
        textAnchor='middle'
        className='fill-muted-foreground text-[10px] font-medium'
      >
        P
      </text>
      <text
        x={PLR_HIGH_COLUMN_X}
        y={HEADER_BASELINE_Y}
        textAnchor='middle'
        className='fill-muted-foreground text-[10px] font-medium'
      >
        PLr
      </text>
      <text
        x={PLR_HIGH_COLUMN_X}
        y={HEADER_BASELINE_Y + 11}
        textAnchor='middle'
        className='fill-muted-foreground text-[9px]'
      >
        {highOccurrenceLabel}
      </text>
      <text
        x={PLR_LOW_COLUMN_X}
        y={HEADER_BASELINE_Y}
        textAnchor='middle'
        className='fill-muted-foreground text-[10px] font-medium'
      >
        PLr
      </text>
      <text
        x={PLR_LOW_COLUMN_X}
        y={HEADER_BASELINE_Y + 11}
        textAnchor='middle'
        className='fill-muted-foreground text-[9px]'
      >
        {lowOccurrenceLabel}
      </text>

      {GRAPH_EDGES.map((edge) => {
        const fromNode = NODE_BY_ID_OFFSET.get(edge.from)
        const toNode = NODE_BY_ID_OFFSET.get(edge.to)
        if (fromNode == null || toNode == null) {
          return null
        }

        const edgeKey = `${edge.from}->${edge.to}`
        const isActive = activeEdgeKeys.has(edgeKey)
        const { x1, y1, x2, y2 } = getEdgeMidpoints(fromNode, toNode)

        return (
          <line
            key={edgeKey}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            className={cn(
              isActive ? 'stroke-foreground' : 'stroke-muted-foreground/35'
            )}
            strokeWidth={isActive ? 2 : 1}
          />
        )
      })}

      {GRAPH_NODES_OFFSET.map((node) => {
        const isActive = activeNodeIds.has(node.id)
        const isPlrNode = node.plrKey != null
        const isFinalPlr = isPlrNode && node.id === finalPlrNodeId
        const isIntermediatePlr =
          isPlrNode && isActive && !isFinalPlr && probabilityOfOccurrence === 'LOW'
        let fill = 'var(--background)'
        let textFill = 'currentColor'
        let strokeClass = isActive
          ? 'stroke-foreground'
          : 'stroke-muted-foreground/50'
        let strokeColor: string | undefined
        let strokeWidth = isActive ? 2 : 1

        if (isPlrNode && node.plrKey != null) {
          if (isFinalPlr) {
            fill = PLR_FILL[node.plrKey]
            textFill = PLR_TEXT[node.plrKey]
            strokeWidth = 2.5
          } else if (isIntermediatePlr) {
            fill = 'var(--muted)'
            strokeColor = PLR_FILL[node.plrKey]
            strokeClass = ''
            strokeWidth = 2
          } else if (!isActive) {
            strokeColor = PLR_FILL[node.plrKey]
            strokeClass = ''
          }
        } else if (isActive) {
          fill = 'var(--muted)'
        }

        const rectX = node.centerX - NODE_WIDTH / 2
        const rectY = node.centerY - NODE_HEIGHT / 2

        return (
          <g key={node.id}>
            <rect
              x={rectX}
              y={rectY}
              width={NODE_WIDTH}
              height={NODE_HEIGHT}
              rx={4}
              fill={fill}
              stroke={strokeColor}
              strokeOpacity={strokeColor != null ? 0.5 : undefined}
              className={strokeClass}
              strokeWidth={strokeWidth}
            />
            <text
              x={node.centerX}
              y={node.centerY + 4}
              textAnchor='middle'
              className={cn(
                'text-[11px] font-semibold',
                isFinalPlr && 'font-bold'
              )}
              fill={textFill}
            >
              {node.label}
            </text>
          </g>
        )
      })}
      </svg>
      {parameterNodes.map((node) => {
        const tooltip = nodeTooltips[node.label]
        const rectX = node.centerX - NODE_WIDTH / 2
        const rectY = node.centerY - NODE_HEIGHT / 2

        return (
          <Tooltip key={`tooltip-${node.id}`}>
            <TooltipTrigger asChild>
              <button
                type='button'
                className='absolute cursor-help rounded border-0 bg-transparent p-0 outline-none focus-visible:ring-2 focus-visible:ring-ring'
                style={{
                  left: `${(rectX / SVG_WIDTH) * 100}%`,
                  top: `${(rectY / SVG_HEIGHT) * 100}%`,
                  width: `${(NODE_WIDTH / SVG_WIDTH) * 100}%`,
                  height: `${(NODE_HEIGHT / SVG_HEIGHT) * 100}%`,
                }}
                aria-label={tooltip}
              />
            </TooltipTrigger>
            <TooltipContent side='top' className='max-w-sm text-left'>
              {tooltip}
            </TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}
